// Helpers for ROS 2 action goal ids.
//
// On the wire (CDR, unique_identifier_msgs/UUID) a goal id is a uint8[16]. Humans and
// ROS 2 CLIs (`ros2 action send_goal`) use the canonical 8-4-4-4-12 hex string. These
// helpers convert between the two so callers can correlate goals across nodes by id.

/** Canonical UUID string (e.g. "282caf42-d0e5-4477-a503-471100de8c26") -> uint8[16]. */
export function uuidStringToBytes(uuid: string): number[] {
  const hex = uuid.replace(/-/g, "");
  if (hex.length !== 32) {
    throw new Error(`Invalid UUID string "${uuid}": expected 32 hex chars`);
  }
  const bytes: number[] = [];
  for (let i = 0; i < hex.length; i += 2) {
    bytes.push(parseInt(hex.substr(i, 2), 16));
  }
  return bytes;
}

/** uint8[16] (array, Uint8Array, or CDR-decoded array) -> canonical UUID string. */
export function uuidBytesToString(bytes: ArrayLike<number>): string {
  if (bytes.length !== 16) {
    throw new Error(`Invalid UUID bytes: expected length 16, got ${bytes.length}`);
  }
  let hex = "";
  for (let i = 0; i < 16; i++) {
    hex += (bytes[i] & 0xff).toString(16).padStart(2, "0");
  }
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20
  )}-${hex.slice(20, 32)}`;
}

/** A fresh random (v4) goal id as uint8[16]. */
export function randomUuidBytes(): number[] {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40; // version 4
  b[8] = (b[8] & 0x3f) | 0x80; // variant
  return Array.from(b);
}

/** A fresh random (v4) goal id as a canonical UUID string. */
export function randomUuidString(): string {
  return uuidBytesToString(randomUuidBytes());
}
